/**
 * Gb74ReportBlocks - 個人レポートの表示ブロック (画面 / PDF 共用)
 *
 * Gb74PersonalReport (画面: 縦1カラム・4セクション) と
 * Gb74PersonalReportPdf (PDF: A4横2ページ) は、同じ中身を別の配置で見せる。
 * 二重管理を避けるため、セクションの「中身」だけをここに部品として切り出す。
 *
 * クラス名は Gb74PersonalReport.scss のものをそのまま使う (見た目は両者で共通)。
 * PDF 側は .gb74rp のスコープで寸法だけを上書きする。
 */
import React from 'react';
import { AXIS_REPORT, type AxisKey, type RoleFitness } from '../utils/gb74Report';
import { DesignRadarChart } from './DesignRadarChart';

/** 01 ヘッダー: ロゴ + 回答日/会社名/部署名/氏名 */
export interface Gb74ReportHeaderProps {
  /** 回答日 (例: 2026.07.15) */
  dateText?: string;
  companyName?: string;
  departmentName?: string;
  userName?: string;
}

export const Gb74ReportHeader: React.FC<Gb74ReportHeaderProps> = ({
  dateText,
  companyName,
  departmentName,
  userName,
}) => (
  <header className="gb74r-d1-header">
    <div className="gb74r-d1-logo">
      <img src="/assets/images/logo.svg" alt="GB人財" />
      <span className="gb74r-d1-logo-ttl">GBタイプ診断結果</span>
    </div>
    <ul className="gb74r-d1-profile">
      {dateText && <li className="gb74r-d1-profile-item">{dateText}</li>}
      {companyName && <li className="gb74r-d1-profile-item">{companyName}</li>}
      {departmentName && <li className="gb74r-d1-profile-item">{departmentName}</li>}
      {userName && <li className="gb74r-d1-profile-item">{userName}</li>}
    </ul>
  </header>
);

/** 01 タイプ名見出し (あなたのGBタイプは / 〇〇タイプ) */
export const Gb74TypeHeading: React.FC<{ gbType: string }> = ({ gbType }) => (
  <hgroup className="gb74r-d1-hgroup">
    <p className="gb74r-d1-hgroup-txt">あなたのGBタイプは</p>
    <h4 className="gb74r-d1-hgroup-ttl">{gbType}</h4>
  </hgroup>
);

/** 象限の配置 (design: A=左上 / B=左下 / C=右下 / D=右上)。valFirst=スコアを左に出す */
const QUADRANTS: Array<{ key: AxisKey; pos: 'a' | 'b' | 'c' | 'd'; valFirst: boolean }> = [
  { key: 'A', pos: 'a', valFirst: true },
  { key: 'B', pos: 'b', valFirst: true },
  { key: 'C', pos: 'c', valFirst: false },
  { key: 'D', pos: 'd', valFirst: false },
];

/** レーダー四隅の軸名 (左上A論理 / 右上D創造 / 右下C共感 / 左下B着実) */
const AXIS_CORNERS: Array<{ key: AxisKey; cls: string }> = [
  { key: 'A', cls: '_tl' },
  { key: 'D', cls: '_tr' },
  { key: 'C', cls: '_br' },
  { key: 'B', cls: '_bl' },
];

/** 01 ダイヤモンド本体: 中央レーダーチャート + 四隅の軸ボックス */
export const Gb74DiamondRadar: React.FC<{ axis: Record<AxisKey, number> }> = ({ axis }) => (
  <div className="gb74r-d1-radar">
    <div className="gb74r-d1-chart">
      <DesignRadarChart scoreA={axis.A} scoreB={axis.B} scoreC={axis.C} scoreD={axis.D} />
      {AXIS_CORNERS.map(({ key, cls }) => (
        // 色は SCSS 側で青に統一する（軸ごとの色分けはやめた）
        <span key={key} className={`gb74r-d1-axname ${cls}`}>
          {AXIS_REPORT[key].letter} {AXIS_REPORT[key].axisLabel}
        </span>
      ))}
    </div>

    {QUADRANTS.map(({ key, pos, valFirst }) => {
      const c = AXIS_REPORT[key];
      const score = axis[key] ?? 0;
      const valNode = (
        <span className="gb74r-d1-box-ttl-val">
          <span className="gb74r-d1-box-ttl-val-lg">{score}</span>/10
        </span>
      );
      const txtNode = (
        <span className="gb74r-d1-box-ttl-txt">
          <span className="gb74r-d1-box-ttl-txt-lg">{c.letter}</span>
          {c.axisLabel}
        </span>
      );
      return (
        <div className={`gb74r-d1-box _pos-${pos}`} key={key}>
          <h5 className="gb74r-d1-box-ttl">
            {valFirst ? (
              <>
                {valNode}
                {txtNode}
              </>
            ) : (
              <>
                {txtNode}
                {valNode}
              </>
            )}
          </h5>
          <dl className="gb74r-d1-box-dl">
            <div className="gb74r-d1-box-dl-inner">
              <dt className="gb74r-d1-box-dl-theme">
                {c.letter} {c.typeLabel}の特徴
              </dt>
              <dd className="gb74r-d1-box-dl-des">
                <ul className="gb74r-d1-box-disc">
                  {c.bullets.map((b) => (
                    <li className="gb74r-d1-box-disc-item" key={b}>
                      {b}
                    </li>
                  ))}
                </ul>
                <ul className="gb74r-d1-box-chips">
                  {c.chips.map((chip) => (
                    <li className="gb74r-d1-box-chips-item" key={chip}>
                      {chip}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          </dl>
        </div>
      );
    })}
  </div>
);

/** §02 スタイルカードのアイコン種別 (思考=思考スタイル / 行動=業務上の行動スタイル) */
const styleIcon = (title: string): string =>
  title.includes('思考') ? ' _thought' : title.includes('行動') ? ' _action' : '';

export interface Gb74StyleEntry {
  title: string;
  theme?: string;
  text?: string;
}

/** 02 思考と行動のスタイル: 1項目 */
export const Gb74StyleItem: React.FC<{ style: Gb74StyleEntry; hasDivider?: boolean }> = ({
  style,
  hasDivider,
}) => (
  <div className={`gb74r-d-style-item${hasDivider ? ' has-divider' : ''}`}>
    <div className={`gb74r-d-style-label${styleIcon(style.title)}`}>{style.title}</div>
    {style.theme && <div className="gb74r-d-style-theme">{style.theme}</div>}
    {style.text && <div className="gb74r-d-style-des">{style.text}</div>}
  </div>
);

/** 役割適性スコアの色分け (高い順に 4 段階) */
const roleClass = (s: number): string =>
  s >= 70 ? 'role-high' : s >= 55 ? 'role-mid' : s >= 40 ? 'role-cond' : 'role-low';

/** 03 役割適性: バーリスト */
/**
 * 03 役割適性のリスト。
 *
 * 【数値を出さない理由】測定のばらつきが大きく、点数の細かい差に意味が無いため。
 * 本人の傾向が変わっていなくても再回答でスコアは平均7.2点動き（5点以上動く人が61%）、
 * 1位の役割が入れ替わる人が41%いる。1点刻みの表示は精度を超えた印象を与えるので、
 * 程度が伝わるバーだけを残している。
 *
 * 【2グループに分ける理由】上位3/下位3という「まとまり」なら2つ以上的中する率が96%
 * あり信頼できる一方、3位と4位の差が5点以下の人が60%おり順位の境目は誤差。
 * そのため順位を断定せず、グループとして提示する。
 * 下位側も隠さず出す（3つ挙げれば残りは推測できるため、伏せる方が誤解を招く）。
 * ただし「苦手」ではなく「補うとよい」と表現する。
 */
const ROLE_TOP_COUNT = 3;

const roleItem = (role: RoleFitness) => (
  <div className="gb74r-role-item" key={role.name}>
    <div className="gb74r-role-name">{role.name}</div>
    <div className="gb74r-role-bar-bg">
      <div
        className={`gb74r-role-bar ${roleClass(role.score)}`}
        style={{ width: `${role.score}%` }}
      />
    </div>
  </div>
);

export const Gb74RoleList: React.FC<{ roles: RoleFitness[] }> = ({ roles }) => {
  // roles は getRoleFitness が降順ソート済み
  const top = roles.slice(0, ROLE_TOP_COUNT);
  const rest = roles.slice(ROLE_TOP_COUNT);
  return (
    <>
      <div className="gb74r-role-group">向いていそうな役割</div>
      {top.map(roleItem)}
      {rest.length > 0 && (
        <>
          <div className="gb74r-role-group _sub">補うとよい役割</div>
          {rest.map(roleItem)}
        </>
      )}
    </>
  );
};

/** 04 気をつけたいこと: 1カード */
export const Gb74CautionCard: React.FC<{ title?: string; text: string }> = ({ title, text }) => (
  <div className="gb74r-d-card gb74r-d-caution">
    {title && <div className="gb74r-d-caution-title">{title}</div>}
    <div className="gb74r-d-caution-text">{text}</div>
  </div>
);

/** 03 役割適性の注記 (画面 / PDF 共通) */
export const ROLE_NOTE =
  '※ 能力の高低ではなく「その役割に向いているか」を表します。6つの差は小さく、並び順は目安です。';

/** 04 気をつけたいことの注記 (画面 / PDF 共通) */
export const CAUTION_NOTE =
  '※ これは個人の問題ではなく、このタイプに共通して見られる傾向です。チームの体制や仕組みで自然に防げます。';
